import { GuardedType, isArray, IsInterface, isNullable, isOptional } from "generic-type-guard";

import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import {
	AddDocumentJsonRpc2Response,
	DocumentSpec,
	GetDocumentJsonRpc2Response,
	JsonRpc2Request,
	JsonRpc2Response,
	JsonRpc2ResponseOK,
	Query,
	QueryJsonRpc2Response
} from "@com.mgmtp.a12.dataservices/dataservices-access";
import { RestRequestPayload } from "@com.mgmtp.a12.utils/utils-connector/lib/main";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { requestRetryAttempts, requestRetryDelayMs } from "../config";
import { EmailDto } from "../types/email";
import { EPaymentStartRequestDto } from "../types/epayment";

import { GERMAN_LOCALE_STRING } from "./localeUtils";
import {
	fetchFromServerConnector,
	fetchFromServerConnectorWithRetry,
	HttpResponseError,
	isGatewayTimeoutResponse
} from "./restHelper";
import { isArrayOf, isObject, isString } from "./typeGuardUtils";
import { isDocumentWithRoot } from "./loadAppmodelTypeguards";

const logger = LoggerFactory.getLogger("rpc-helper");

export class TaskCompletionOutcomeUnknownError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "TaskCompletionOutcomeUnknownError";
	}
}

export async function completeTask(docRef: string): Promise<void> {
	const request = createRequest("COMPLETE_TASK", { docRef });
	let response: unknown;
	try {
		response = await fetchFromServerConnector(request);
	} catch (error) {
		const status = getHttpStatus(error);
		if (status !== undefined && status >= 400 && status < 500) {
			throw error;
		}
		throw new TaskCompletionOutcomeUnknownError("The task completion outcome is unknown.", { cause: error });
	}

	if (isGatewayTimeoutResponse(response)) {
		throw new TaskCompletionOutcomeUnknownError("Gateway timeout reported in the JSON-RPC response.");
	}
	if (!isJsonRpc2ResponseArray(response) || response.length !== 1) {
		throw new TaskCompletionOutcomeUnknownError("The server response cannot be interpreted.");
	}

	if (!hasNoError(response)) {
		const rpcErrors = response.filter(JsonRpc2Response.error.isInstance).map(e => e.error);
		throw new Error("The rpc response has errors:\n" + JSON.stringify(rpcErrors), { cause: rpcErrors });
	}
}

function getHttpStatus(error: unknown): number | undefined {
	if (error instanceof HttpResponseError) {
		return error.status;
	}
	if (!error || typeof error !== "object") {
		return undefined;
	}
	const status = (error as { status?: unknown }).status;
	const response = (error as { response?: unknown }).response;
	const nestedStatus =
		response && typeof response === "object" ? (response as { status?: unknown }).status : undefined;
	const value = status ?? nestedStatus;
	if (typeof value === "number") {
		return value;
	}
	if (typeof value === "string" && /^\d{3}$/.test(value)) {
		return Number(value);
	}
	return undefined;
}

export async function modifyDocument(docRef: string, document: object): Promise<void> {
	const request = createRequest("MODIFY_DOCUMENT", { docRef, document });
	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		const rpcErrors = jsonRpcResponse.filter(JsonRpc2Response.error.isInstance).map(e => e.error);
		return Promise.reject(
			new Error("The rpc response has errors:\n" + JSON.stringify(rpcErrors), { cause: rpcErrors })
		);
	}

	return Promise.resolve();
}

export async function getDocument(docRef: string): Promise<GetDocumentJsonRpc2Response["result"] | undefined> {
	const request = createRequest("GET_DOCUMENT", { docRef });
	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		return Promise.reject(jsonRpcResponse.filter(JsonRpc2Response.error.isInstance));
	}

	if (jsonRpcResponse.length == 0 || !GetDocumentJsonRpc2Response.isInstance(jsonRpcResponse[0])) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	return jsonRpcResponse[0].result;
}

export async function addDocument(
	documentModelName: string,
	document: object
): Promise<AddDocumentJsonRpc2Response["result"]> {
	const request = createRequest("ADD_DOCUMENT", { documentModelName, document, locale: GERMAN_LOCALE_STRING });
	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		return Promise.reject(jsonRpcResponse.filter(JsonRpc2Response.error.isInstance));
	}

	if (jsonRpcResponse.length == 0 || !AddDocumentJsonRpc2Response.isInstance(jsonRpcResponse[0])) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	return jsonRpcResponse[0].result;
}

/**
 * Async function used to fetch the first fitting document from a12 using a JsonRpc2 request.
 * @returns The DocumentSpec of the found document or undefined
 */
export async function query(query: Query.QueryRoot): Promise<Query.DocumentTreeResult[] | undefined> {
	const request = createRequest("QUERY", { query });

	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		return Promise.reject(jsonRpcResponse.filter(JsonRpc2Response.error.isInstance));
	}

	if (
		jsonRpcResponse.length !== 1 ||
		!QueryJsonRpc2Response.isInstance(jsonRpcResponse[0]) ||
		!isArrayOf(Query.DocumentTreeResult.isInstance)(jsonRpcResponse[0].result.entries)
	) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	return jsonRpcResponse[0].result.entries;
}

export async function deleteDocument(docRef: string): Promise<void> {
	const request = createRequest("DELETE_DOCUMENT", { docRef, locale: "de_DE" });
	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		return Promise.reject(
			new Error(
				"The rpc response has errors:\n" +
					JSON.stringify(jsonRpcResponse.filter(JsonRpc2Response.error.isInstance).map(e => e.error))
			)
		);
	}

	return Promise.resolve();
}

export async function hasRole(docRef: string): Promise<boolean> {
	const request = createRequest("MODULF_TASK_HAS_PERMISSION", { docRef: docRef });

	const jsonRpcResponse = await fetchFromServerConnectorWithRetry(request, {
		attempts: requestRetryAttempts,
		delayMs: requestRetryDelayMs,
		shouldRetryResponse: isGatewayTimeoutResponse,
		fetchRequest: fetchFromServerConnector
	});

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (jsonRpcResponse.length == 0 || !JsonRpc2Response.isInstance(jsonRpcResponse[0])) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	const response = jsonRpcResponse[0];

	if (response.error) {
		logger.error(`Could not get task permission of document reference ${docRef}.`);
		throw new Error("The rpc response has errors:\n" + JSON.stringify(response.error), {
			cause: response.error
		});
	}
	if (typeof response.result !== "boolean") {
		throw new Error("The server response cannot be interpreted!");
	}
	return response.result;
}

/**
 * Sends a request to the server for sending an email.
 */
export async function sendEmail(docRef: string, document: object, emailProperties: EmailDto): Promise<DocumentSpec> {
	return callDocumentSpecRpc("MODULF_SEND_EMAIL", { docRef, document, emailProperties });
}

/**
 * Legt bei PD Payment einen neuen ePayment-Zahlungsvorgang an (siehe {@code StartEPaymentOperation}).
 */
export async function startEPayment(
	docRef: string,
	document: object,
	paymentGroupPath: string,
	request: EPaymentStartRequestDto
): Promise<DocumentSpec> {
	return callDocumentSpecRpc("MODULF_START_EPAYMENT", { docRef, document, paymentGroupPath, request });
}

/**
 * Fragt den Status eines zuvor angelegten ePayment-Zahlungsvorgangs ab und übernimmt einen
 * erfolgreichen Zahlungsabschluss in das Dokument (siehe {@code StatusEPaymentOperation}).
 */
export async function getEPaymentStatus(
	docRef: string,
	paymentGroupPath: string,
	paymentKey: string
): Promise<DocumentSpec> {
	return callDocumentSpecRpc("MODULF_GET_EPAYMENT_STATUS", { docRef, paymentGroupPath, paymentKey });
}

/**
 * Calls an RPC operation that returns exactly one {@link DocumentSpec} and rejects with the RPC errors
 * (so callers can surface the server-side message) or with a generic error for uninterpretable responses.
 */
async function callDocumentSpecRpc(operation: string, params: object): Promise<DocumentSpec> {
	const response = await fetchFromServerConnector(createRequest(operation, params));

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}
	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}
	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result;
}

/**
 * Sends a request to the server for archiving an antrag.
 */
export async function archive(
	documentReference: string,
	pathToArchiveGroup: string,
	archiveContentType: string
): Promise<DocumentSpec> {
	const request = createRequest("MODULF_ARCHIVE", { documentReference, pathToArchiveGroup, archiveContentType });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result;
}

/**
 * Sends a request to the server for archiving an antrag.
 */
export async function getChangelog(mainDocumentReference: string): Promise<DocumentSpec> {
	const request = createRequest("MODULF_GET_CHANGELOG", { mainDocumentReference });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result;
}

/**
 * Sends a request to the server for starting a process for a new antrag
 */
export async function modulfStartProcess(
	documentModelName: string,
	document: object,
	processDefinition: string
): Promise<string> {
	const request = createRequest("MODULF_START_PROCESS_OPERATION", { documentModelName, document, processDefinition });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!isString(response[0].result.docRef)) {
		return Promise.reject(new Error("The server response cannot be interpreted."));
	}

	return response[0].result.docRef;
}

export async function getLatestApplicationVersion(applicationKey: string): Promise<string> {
	const request = createRequest("MODULF_GET_LATEST_APPLICATION_VERSION", { applicationKey });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1 || !isString(response[0].result)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result;
}

/**
 * Sends a request to the server for starting a process for a new antrag
 */
export async function modulfModulfCopyFachverfahrenData(documentReference: string): Promise<CopyProcessResponse> {
	const request = createRequest("MODULF_COPY_FACHVERFAHREN_DATA", { documentReference });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!isObject(response[0].result)) {
		return Promise.reject(new Error("The server response cannot be interpreted."));
	}

	if (!isCopyProcessResponse(response[0].result)) {
		return Promise.reject(new Error("The server response does not match the expected type"));
	}

	return response[0].result;
}

export const isDomainFieldDescriptor = new IsInterface()
	.withProperties({
		path: isString,
		label: isString,
		prettyDisplayPath: isString
	})
	.get();

export const isCopyProcessResponse = new IsInterface()
	.withProperties({
		document: isDocumentWithRoot,
		cleanedModelFields: isArray(isDomainFieldDescriptor)
	})
	.get();

export const isLockDocumentOperationResponse = new IsInterface()
	.withProperties({
		processLockedByUserId: isOptional(isNullable(isString)),
		processLockedByUserFullName: isOptional(isNullable(isString)),
		processLockedAt: isOptional(isNullable(isString)),
		processLockExpiration: isOptional(isNullable(isString)),
		errorCode: isOptional(isNullable(isString)),
		errorMessage: isOptional(isNullable(isString))
	})
	.get();

export const isValidLockDocumentOperationResponse = new IsInterface()
	.withProperties({
		processLockedByUserId: isString,
		processLockedByUserFullName: isString,
		processLockedAt: isString,
		processLockExpiration: isString
	})
	.get();

export type CopyProcessResponse = GuardedType<typeof isCopyProcessResponse>;

export type LockDocumentOperationResponse = GuardedType<typeof isLockDocumentOperationResponse>;

/**
 * Sends the content of an XML or JSON file to the server for parsing.
 * The server automatically determines the correct mapper based on the mapping configuration.
 */
export async function parseImportFile(docRef: string, document: object, fileContent: string): Promise<object> {
	const request = createRequest("MODULF_PARSE_XML", { docRef, document, fileContent });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result;
}

/** @deprecated use {@link parseImportFile} */
export async function parseXml(docRef: string, document: object, xmlString: string): Promise<object> {
	return parseImportFile(docRef, document, xmlString);
}

export async function extractStammdaten(docRef: string): Promise<object> {
	const request = createRequest("MODULF_EXTRACT_STAMMDATEN", { docRef });
	const jsonRpcResponse = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(jsonRpcResponse)) {
		return Promise.reject(new Error("The server response cannot be interpreted!"));
	}

	if (!hasNoError(jsonRpcResponse)) {
		return Promise.reject(jsonRpcResponse.filter(JsonRpc2Response.error.isInstance));
	}

	return jsonRpcResponse[0].result;
}

/**
 * Sends a request to the server to invalidate a client notification
 */
export async function modulfInvalidateClientNotification(docRef: string, notificationUuids: string[]): Promise<void> {
	const request = createRequest("MODULF_INVALIDATE_NOTIFICATION", { docRef, notificationUuids });
	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}
}

export async function deletePrintDocument(docRef: string, pathToFileAttachment?: string): Promise<void> {
	const request = createRequest("MODULF_DELETE_PRINT_DOCUMENT", {
		docRef,
		pathToFileAttachment
	});

	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve();
}

export async function printDocument(
	docRef: string,
	filesToPrint: {
		printModelId: string;
		pathToFileAttachments?: string;
		attachmentDocumentType?: string;
		fileNameFormat?: string;
	}[]
): Promise<void> {
	const request = createRequest("MODULF_PRINT_DOCUMENT", {
		docRef,
		printProperties: {
			filesToPrint
		}
	});

	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve();
}

/**
 * Startet den Signiervorgang für ein generiertes PDF.
 *
 * @returns Die {@code pageVisitUrl} der sign-me-Seite, die der Nutzer für die
 *          2FA-Bestätigung aufrufen muss (bei D-Trust immer gesetzt),
 *          oder {@code null} wenn kein Browser-Redirect nötig ist.
 */
export async function signDocument(
	docRef: string,
	pathToFileAttachments: string,
	signaturEinbetten: boolean,
	signaturPosition?: string
): Promise<string | null> {
	const request = createRequest("MODULF_SIGN_DOCUMENT", {
		docRef,
		signProperties: {
			pathToFileAttachments,
			signaturEinbetten,
			...(signaturPosition ? { signaturPosition } : {})
		}
	});

	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	const result = JsonRpc2Response.isInstance(response[0]) ? response[0].result : null;
	return (result as { pageVisitUrl?: string } | null)?.pageVisitUrl ?? null;
}

/**
 * Fragt den Datasign-Signaturstatus ab und schließt den Signiervorgang bei Erfolg ab.
 * Der Server ersetzt das Attachment durch das signierte PDF und setzt die Metadatenfelder.
 *
 * @returns {@code true} wenn der Signiervorgang abgeschlossen und das Dokument finalisiert wurde,
 *          {@code false} wenn der Vorgang noch aussteht.
 */
export async function getSignStatus(docRef: string, pathToFileAttachments: string): Promise<boolean> {
	const request = createRequest("MODULF_GET_SIGN_STATUS", {
		docRef,
		pathToFileAttachments
	});

	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return response[0].result === true;
}

export async function createGebuehrenforderung(
	document: object,
	docRef: string,
	path: string,
	mappingConfigId: string,
	attachmentId?: string | null
): Promise<Activity.Data.Document | Error> {
	const request = createRequest("MODULF_GEBUEHRENFORDERUNG", {
		document,
		docRef,
		path,
		mappingConfigId,
		attachmentId
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.document);
}

export async function createXFinanzDatei(
	document: object,
	docRef: string,
	path: string,
	xfinanzGroupPath: string,
	mappingConfigId: string
): Promise<Activity.Data.Document | Error> {
	const request = createRequest("MODULF_XFINANZ_DATEI_ERSTELLEN", {
		document,
		docRef,
		path,
		xfinanzGroupPath,
		mappingConfigId
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.document);
}

export async function deleteXFinanzDatei(docRef: string, pathToFileAttachment?: string): Promise<void> {
	const request = createRequest("MODULF_DELETE_XFINANZ_DATEI", {
		docRef,
		pathToFileAttachment
	});

	const response = await fetchFromServerConnector(request);

	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve();
}

export async function selectOpenRegisterRecord(
	document: object,
	docRef: string,
	path: string,
	recordId: string
): Promise<Activity.Data.Document | Error> {
	const request = createRequest("MODULF_SELECT_RECORD", {
		document,
		docRef,
		path,
		recordId
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.document);
}

export async function upsertOpenRegisterRecord(
	document: object,
	docRef: string,
	path: string,
	mappingConfigId: string
): Promise<Activity.Data.Document | Error> {
	const request = createRequest("MODULF_OPENREGISTER_UPSERT", {
		document,
		docRef,
		path,
		mappingConfigId
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.document);
}

export async function loadAndMapOpenRegisterRecord(
	document: object,
	docRef: string,
	path: string,
	mappingConfigId: string,
	recordId?: string
): Promise<Activity.Data.Document | Error> {
	const request = createRequest("MODULF_OPENREGISTER_LOAD_RECORD", {
		document,
		docRef,
		path,
		mappingConfigId,
		recordId
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.document);
}

export interface OpenRegisterRecordPreviewRow {
	recordId: string;
	fields: OpenRegisterRecordPreviewField[];
}

export interface OpenRegisterRecordPreviewField {
	label: string;
	value: string | null;
}

export interface OpenRegisterRecordPreviewPage {
	items: OpenRegisterRecordPreviewRow[];
	pageIndex: number;
	pageSize: number;
	totalItems: number;
	hasNext: boolean;
}

export async function searchOpenRegisterRecords(
	docRef: string,
	path: string,
	mappingConfigId: string,
	pageIndex: number,
	query: string
): Promise<OpenRegisterRecordPreviewPage> {
	const request = createRequest("MODULF_OPENREGISTER_SEARCH_RECORDS", {
		docRef,
		path,
		mappingConfigId,
		pageIndex,
		query
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result as unknown as OpenRegisterRecordPreviewPage);
}

export async function queryLinkedProcessed(docRef: string): Promise<Query.DocumentTreeResult[] | undefined> {
	const request = createRequest("MODULF_QUERY_LINKED_PROCESSES", {
		docRef
	});

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	return Promise.resolve(response[0].result.entries);
}

function createRequest(method: string, params: Map<string, object> | object): RestRequestPayload {
	const request = JsonRpc2Request.build([{ id: 1, jsonrpc: "2.0", method, params }]);
	return {
		...request,
		customHeaders: [...(request.customHeaders ?? []), ["Accept-Language", "de"]]
	};
}

function hasNoError(response: JsonRpc2Response[]): response is JsonRpc2ResponseOK[] {
	return !JsonRpc2Response.hasErrors(response);
}

export function isJsonRpc2ResponseArray(value: unknown): value is JsonRpc2Response[] {
	return Array.isArray(value) && value.every(x => JsonRpc2Response.isInstance(x));
}

export async function lockDocumentOperation(
	docRef: string,
	method: string,
	userId: string,
	userFullName: string
): Promise<LockDocumentOperationResponse> {
	const request = createRequest("MODULF_DOCUMENT_LOCK", { docRef, method, userId, userFullName });

	const response = await fetchFromServerConnector(request);
	if (!isJsonRpc2ResponseArray(response)) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!hasNoError(response)) {
		return Promise.reject(response.filter(JsonRpc2Response.error.isInstance));
	}

	if (response.length !== 1) {
		return Promise.reject(new Error("The server response cannot be interpreted"));
	}

	if (!isLockDocumentOperationResponse(response[0].result)) {
		return Promise.reject(new Error("The server response does not match the expected type"));
	}

	return response[0].result;
}
